'use client';
import React from 'react';
import Link from 'next/link';
import { CheckCircle2, XCircle, RotateCcw, BookOpen } from 'lucide-react';
import { PracticeResult } from './PracticeSessionScreen';

interface Question {
  id: string;
  typeCode: string;
  typeLabel: string;
  question: string;
  correctAnswer: string;
  distractorTrap: string;
}

interface Props {
  questions: Question[];
  results: PracticeResult[];
  onRestart: () => void;
}

export default function PracticeSummary({ questions, results, onRestart }: Props) {
  const correct = results.filter((r) => r.correct).length;
  const total = results.length;
  const accuracy = Math.round((correct / total) * 100);
  const avgTime = Math.round(results.reduce((a, r) => a + r.timeSpent, 0) / total);
  const avgConf = (results.reduce((a, r) => a + r.confidence, 0) / total).toFixed(1);
  const errors = results.filter((r) => !r.correct);

  const bandImpact = accuracy >= 70 ? '+0.5 band trajectory' : accuracy >= 55 ? 'Neutral — maintain pace' : '−0.5 band risk — review errors';
  const bandColor = accuracy >= 70 ? 'text-accent' : accuracy >= 55 ? 'text-info' : 'text-danger';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6 animate-slide-up">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Session Complete</h1>
          <p className="text-sm text-muted-foreground mt-1">Matching Headings + YN/NG adaptive drill · {total} questions</p>
        </div>

        {/* Score overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { id: 'sum-acc', label: 'Accuracy', value: `${accuracy}%`, sub: `${correct}/${total} correct`, color: accuracy >= 58 ? 'text-accent' : 'text-warning' },
            { id: 'sum-time', label: 'Avg Time/Q', value: `${avgTime}s`, sub: avgTime > 90 ? 'Over 90s target' : 'Within target', color: avgTime > 90 ? 'text-warning' : 'text-accent' },
            { id: 'sum-conf', label: 'Avg Confidence', value: `${avgConf}/5`, sub: 'Self-reported', color: 'text-foreground' },
            { id: 'sum-band', label: 'Band Impact', value: bandImpact.split(' ')[0], sub: bandImpact.split(' ').slice(1).join(' '), color: bandColor },
          ].map((stat) => (
            <div key={stat.id} className="card-base p-4 text-center">
              <p className="metric-label mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold tabular-nums ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
            </div>
          ))}
        </div>

        {/* Error review */}
        {errors.length > 0 && (
          <div className="card-base p-5">
            <div className="flex items-center gap-2 mb-4">
              <XCircle size={16} className="text-danger" />
              <h2 className="section-header">Errors to Review ({errors.length})</h2>
            </div>
            <div className="space-y-3">
              {errors.map((err) => {
                const q = questions.find((q) => q.id === err.questionId);
                if (!q) return null;
                return (
                  <div key={`err-${err.questionId}`} className="p-3.5 rounded-xl border border-danger/30 bg-danger-bg/30">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="badge-qtype bg-danger-bg text-danger border border-danger/30 text-xs">{q.typeLabel}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{err.timeSpent}s</span>
                    </div>
                    <p className="text-xs font-medium text-foreground mb-2 line-clamp-2">{q.question}</p>
                    <p className="text-xs text-muted-foreground mb-1.5">
                      Correct answer: <span className="font-semibold text-foreground">{q.correctAnswer}</span>
                    </p>
                    {err.distractorTrap && (
                      <div className="flex items-start gap-1.5 mt-2">
                        <span className="distractor-trap-label flex-shrink-0">Trap</span>
                        <p className="text-xs text-muted-foreground leading-relaxed">{err.distractorTrap}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Perfect score */}
        {errors.length === 0 && (
          <div className="card-base p-5 flex items-center gap-3 bg-success-bg border-accent/30">
            <CheckCircle2 size={24} className="text-accent flex-shrink-0" />
            <div>
              <p className="font-semibold text-foreground">Perfect session!</p>
              <p className="text-sm text-muted-foreground">All {total} questions correct — difficulty will increase in your next set</p>
            </div>
          </div>
        )}

        {/* Next actions */}
        <div className="flex gap-3 flex-wrap">
          <button onClick={onRestart} className="btn-outline flex items-center gap-2">
            <RotateCcw size={14} />
            New Drill Set
          </button>
          <Link href="/annotated-walkthrough" className="btn-secondary flex items-center gap-2">
            <BookOpen size={14} />
            Review Walkthroughs
          </Link>
          <Link href="/" className="btn-primary flex items-center gap-2">
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}